import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import produce from 'immer';
import { generatePath, hardNavigate, hubspotEditPagePaths } from '../../../utils/urls';
import styled from '../../../components/Theme';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIForm from 'UIComponents/form/UIForm';
import H4 from 'UIComponents/elements/headings/H4';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import { useClaimProfile, useUpdateUser, useUserByAuth, useUsernameIsAvailable } from '../userQueries';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import UISection from 'UIComponents/section/UISection';
import CompanySelect from '../../company/components/CompanySelect';
import IndustrySelect from '../../industry/components/IndustrySelect';
import I18n from 'I18n';
import UICharacterCounter from 'UIComponents/counters/UICharacterCounter';
import UITextArea from 'UIComponents/input/UITextArea';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import Small from 'UIComponents/elements/Small';
import UIInputStaticLabel from 'UIComponents/input/UIInputStaticLabel';
import UISelectableButton from 'UIComponents/button/UISelectableButton';
import SocialInput from '../../../components/SocialInput';
import ProductSelect from '../../../components/ProductSelect';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { Goal } from '../../../consts';
import { isEmpty, isMinimumLength, isValidUsername, isBlocklist } from '../../../utils/validation';
import UIAlert from 'UIComponents/alert/UIAlert';
import AvatarUpload from '../../avatar/components/AvatarUpload';
import { Redirect } from 'react-router-dom';
import { H1 } from 'UIComponents/elements/headings';
import { NavMarker } from 'react-rhumb';
import ErrorMessage from '../../../components/ErrorMessage';
import UILink from 'UIComponents/link/UILink';
import GoogleLocationSelect from '../../geolocation/components/GoogleLocationSelect';
import { createGeoocationEntry } from '../../geolocation/geolocationQueries';
import { useAddCompany } from '../../company/companyQueries';
import { selectLocation } from '../../geolocation/autocomplete';
import Raven from 'Raven';
const MicroCopy = styled(Small).withConfig({
  displayName: "EditUserForm__MicroCopy",
  componentId: "sc-64xji-0"
})(["padding-top:15px;display:block;max-width:530px;"]);
const PersonalInformationSection = styled.section.withConfig({
  displayName: "EditUserForm__PersonalInformationSection",
  componentId: "sc-64xji-1"
})(["display:grid;grid-template-columns:1fr;grid-gap:16px;@media (min-width:", "){grid-template-columns:70% calc(30% - 24px);& > *:nth-child(3){grid-column:span 2;}}& > *{margin:0 !important;}"], props => props.theme.sizes.GRID_BREAKPOINT_MEDIUM);
const Columns = styled.section.withConfig({
  displayName: "EditUserForm__Columns",
  componentId: "sc-64xji-2"
})(["display:grid;grid-template-columns:1fr;grid-gap:16px;@media (min-width:", "){grid-template-columns:", ";}& > *{margin:0 !important;}", ""], props => props.theme.sizes.GRID_BREAKPOINT_MEDIUM, props => props.block ? `1fr 1fr` : `max-content max-content`, props => props.equalHeight && `grid-auto-rows: 1fr;`);
const GoalButton = styled((_ref) => {
  let {
    value,
    onChange
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["value", "onChange"]);

  const handleChange = useCallback(() => {
    onChange(value);
  }, [onChange, value]);
  return /*#__PURE__*/_jsx(UISelectableButton, Object.assign({}, rest, {
    onClick: handleChange
  }));
}).withConfig({
  displayName: "EditUserForm__GoalButton",
  componentId: "sc-64xji-3"
})(["min-width:100%;align-items:flex-start;& > div{top:25px !important;}& > div + div{text-align:left;flex-direction:column;align-items:flex-start !important;padding:12px 20px 0 60px !important;}"]);
const validators = Object.assign({
  displayName: displayName => {
    if (isEmpty(displayName)) {
      return I18n.text('connectDotCom.form.required');
    }

    return null;
  },
  userName: username => {
    if (isEmpty(username)) {
      return I18n.text('connectDotCom.form.required');
    }

    if (!isMinimumLength(username, 3)) {
      return I18n.text('connectDotCom.form.minimumLength', {
        min: 3
      });
    }

    if (!isValidUsername(username)) {
      return I18n.text('connectDotCom.form.invalidUsername');
    }

    if (isBlocklist(username)) {
      return I18n.text('connectDotCom.form.invalidUsername');
    }

    return null;
  }
});
const initialFormState = {
  isDirty: false,
  isValid: true,
  errors: {},
  helpText: {}
};

const useUserForm = () => {
  const [formData, setFormData] = useState(initialFormState);
  const usernameAvailable = useUsernameIsAvailable(formData.userName);
  const resetForm = useCallback(state => {
    setFormData(Object.assign({}, state, {}, initialFormState));
  }, []);
  const updateValue = useCallback((key, value) => {
    setFormData(produce(draft => {
      if (value !== null) {
        draft[key] = value;
        draft.isDirty = true;
        const validator = validators[key];

        if (validator) {
          const error = validator(value);

          if (error) {
            draft.errors[key] = error;
          } else {
            delete draft.errors[key];
          }
        }
      }
    }));
  }, []);
  useEffect(() => {
    setFormData(produce(draft => {
      if (usernameAvailable === 'available') {
        draft.helpText.userName = I18n.text('connectDotCom.form.usernameAvailable');
      } else if (usernameAvailable === 'unavailable') {
        delete draft.helpText.userName;
        draft.errors.userName = I18n.text('connectDotCom.form.usernameUnavailable');
      } else if (usernameAvailable === 'current') {
        delete draft.helpText.userName;
      }
    }));
  }, [usernameAvailable]);
  return {
    formData: Object.assign({}, formData, {
      isValid: Object.values(formData.errors).length === 0
    }),

    getFieldMessage(key) {
      return formData.errors[key] || formData.helpText[key];
    },

    resetForm,
    updateValue
  };
};

const omitKeys = (obj, keys) => {
  const newObject = Object.assign({}, obj);

  for (const k of keys) delete newObject[k];

  return newObject;
};

const EditUserForm = () => {
  const {
    user,
    userLoading
  } = useUserByAuth();
  const {
    updateUser
  } = useUpdateUser();
  const {
    addCompany
  } = useAddCompany();
  const {
    hasClaimedProfile
  } = useClaimProfile();
  const {
    formData,
    resetForm,
    updateValue,
    getFieldMessage
  } = useUserForm();
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const canSubmit = formData.isDirty && formData.isValid;
  useEffect(() => {
    resetForm(user);
  }, [resetForm, user]);
  const handleInputChange = useCallback(e => {
    const stringValue = e.target.value;
    const value = stringValue === 'true' ? true : stringValue === 'false' ? false : stringValue;
    updateValue(e.target.name, value);
  }, [updateValue]);
  const handleTextAreaChange = useCallback(e => {
    updateValue(e.target.name, e.target.value);
  }, [updateValue]);
  const handleCompanyChange = useCallback(company => {
    if (company) {
      updateValue('company', company);
    }
  }, [updateValue]);
  const handleIndustryChange = useCallback(industry => {
    if (industry) {
      updateValue('industry', industry);
    }
  }, [updateValue]);
  const handleGeolocationChange = useCallback(geolocation => {
    if (geolocation) {
      updateValue('geolocation', geolocation);
    }
  }, [updateValue]);
  const handleProductsChange = useCallback(products => {
    if (products) {
      updateValue('products', products);
    }
  }, [updateValue]);
  const handleGoalChange = useCallback(goal => {
    updateValue('networkGoal', goal);
  }, [updateValue]);
  const handleSubmit = useCallback(e => {
    e.preventDefault();
    if (user === null) return;
    if (!canSubmit) return;
    setShowErrorAlert(false);
    const draftUser = omitKeys(formData, [...Object.keys(initialFormState), 'avatarLastUpdated']);
    let addCompanyPromise;
    let selectGeolocationPromise;
    const gplaceId = formData.geolocation && formData.geolocation.gplaceId;

    if (gplaceId) {
      selectGeolocationPromise = selectLocation(gplaceId).then(location => createGeoocationEntry(Object.assign({}, location, {
        displayName: formData.geolocation.displayName
      })));
    }

    if (draftUser.company && `${draftUser.company.id}` === draftUser.company.name) {
      // TODO simplify when async await arrives
      addCompanyPromise = addCompany({
        variables: {
          companyName: draftUser.company.name
        }
      });
    }

    draftUser.public = !!draftUser.public;
    Promise.all([addCompanyPromise, selectGeolocationPromise]).then(([companyResponse, geolocationCreationResponse]) => {
      if (companyResponse && companyResponse.data) {
        draftUser.company = companyResponse.data.addCompany;
      }

      if (geolocationCreationResponse) {
        draftUser.geolocation = geolocationCreationResponse;
      }
    }).then(() => updateUser({
      variables: {
        user: draftUser,
        userName: user.userName
      }
    })).then(response => {
      if (response.data) {
        hardNavigate(hubspotEditPagePaths.user(response.data.updateUserByUsername.userName));
      }
    }).catch(error => {
      Raven.captureException(error);
      setShowErrorAlert(true);
    });
  }, [user, canSubmit, updateUser, formData, addCompany]);

  if (!formData || userLoading) {
    return /*#__PURE__*/_jsx(UIFlex, {
      align: "center",
      justify: "center",
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {})
    });
  }

  if (!hasClaimedProfile) {
    return /*#__PURE__*/_jsx(Redirect, {
      to: generatePath.directory()
    });
  }

  if (!user) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "EDIT_PROFILE_LOAD_FAILURE",
      children: /*#__PURE__*/_jsx(ErrorMessage, {
        type: "error"
      })
    });
  }

  return /*#__PURE__*/_jsx(NavMarker, {
    name: "EDIT_PROFILE_LOADED",
    children: /*#__PURE__*/_jsxs(UIForm, {
      onSubmit: handleSubmit,
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        justify: "between",
        children: [/*#__PURE__*/_jsx(H1, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.editUser.title"
          })
        }), /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(UIButton, {
            "data-test-id": "save-changes",
            use: "primary",
            type: "submit",
            disabled: !canSubmit,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.saveChanges"
            })
          }), /*#__PURE__*/_jsx(UILink, {
            href: hubspotEditPagePaths.user(user.userName),
            className: "m-left-5",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.cancel"
            })
          })]
        })]
      }), showErrorAlert && /*#__PURE__*/_jsx(UIAlert, {
        type: "danger",
        titleText: I18n.text('connectDotCom.editUser.errorTitle'),
        closeable: true,
        onClose: () => {
          setShowErrorAlert(false);
        },
        className: "m-bottom-5",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.editUser.errorMessage"
        })
      }), /*#__PURE__*/_jsx(UISection, {
        className: "m-top-8 m-bottom-4",
        children: /*#__PURE__*/_jsx(AvatarUpload, {
          avatarHash: user.avatarHash,
          title: /*#__PURE__*/_jsx(H4, {
            className: "m-bottom-1",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.profileImage"
            })
          })
        })
      }), /*#__PURE__*/_jsxs(UISection, {
        className: "m-bottom-12",
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.editUser.personalInformation"
          })
        }), /*#__PURE__*/_jsxs(PersonalInformationSection, {
          children: [/*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.displayName"
            }),
            required: true,
            error: Boolean(formData.errors.displayName),
            validationMessage: formData.errors.displayName,
            children: /*#__PURE__*/_jsx(UITextInput, {
              name: "displayName",
              use: "on-dark",
              value: formData.displayName,
              onChange: handleInputChange
            })
          }), /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.pronouns"
            }),
            children: /*#__PURE__*/_jsx(UITextInput, {
              name: "pronouns",
              use: "on-dark",
              value: formData.pronouns,
              onChange: handleInputChange
            })
          }), /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.userName"
            }),
            required: true,
            error: Boolean(formData.errors.userName),
            validationMessage: getFieldMessage('userName'),
            children: /*#__PURE__*/_jsx(UIInputStaticLabel, {
              text: "connect.com/member/",
              children: /*#__PURE__*/_jsx(UITextInput, {
                name: "userName",
                use: "on-dark",
                maxLength: 25,
                value: formData.userName,
                onChange: handleInputChange
              })
            })
          })]
        })]
      }), /*#__PURE__*/_jsxs(UISection, {
        className: "m-bottom-12",
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.editUser.professionalInformation"
          })
        }), /*#__PURE__*/_jsxs(Columns, {
          block: true,
          children: [/*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.jobTitle"
            }),
            children: /*#__PURE__*/_jsx(UITextInput, {
              name: "jobTitle",
              use: "on-dark",
              value: formData.jobTitle,
              onChange: handleInputChange
            })
          }), /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.company"
            }),
            children: /*#__PURE__*/_jsx(CompanySelect, {
              onChange: handleCompanyChange,
              placeholder: I18n.text('connectDotCom.editUser.companyPlaceholder'),
              value: formData.company
            })
          }), /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.industry"
            }),
            children: /*#__PURE__*/_jsx(IndustrySelect, {
              onChange: handleIndustryChange,
              placeholder: I18n.text('connectDotCom.editUser.industryPlaceholder'),
              value: formData.industry
            })
          }), /*#__PURE__*/_jsx(UIFormControl, {
            label: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.location"
            }),
            children: /*#__PURE__*/_jsx(GoogleLocationSelect, {
              onChange: handleGeolocationChange,
              placeholder: I18n.text('connectDotCom.editUser.locationPlaceholder'),
              value: formData.geolocation ? {
                displayName: formData.geolocation.displayName,
                gplaceId: formData.geolocation.gplaceId ? formData.geolocation.gplaceId : formData.geolocation.id.toString()
              } : undefined
            })
          }), /*#__PURE__*/_jsx(UICharacterCounter, {
            counter: "down",
            max: 250,
            value: formData.biography,
            onChange: handleTextAreaChange,
            renderInput: ({
              count,
              value,
              onChange
            }) => /*#__PURE__*/_jsx(UIFormControl, {
              characterCount: count,
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.biography"
              }),
              children: /*#__PURE__*/_jsx(UITextArea, {
                "data-test-id": "biography-text-area",
                use: "on-dark",
                name: "biography",
                value: value,
                onChange: onChange
              })
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(UISection, {
        className: "m-bottom-12",
        children: /*#__PURE__*/_jsxs(Columns, {
          block: true,
          children: [/*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.shareLinks"
              })
            }), /*#__PURE__*/_jsx(UIFormControl, {
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.channels"
              }),
              children: /*#__PURE__*/_jsx("span", {})
            }), /*#__PURE__*/_jsx(SocialInput, {
              name: "linkedinId",
              value: formData.linkedinId,
              onChange: handleInputChange
            }), /*#__PURE__*/_jsx(SocialInput, {
              name: "twitterId",
              value: formData.twitterId,
              onChange: handleInputChange
            }), /*#__PURE__*/_jsx(SocialInput, {
              name: "githubId",
              value: formData.githubId,
              onChange: handleInputChange
            }), /*#__PURE__*/_jsx(SocialInput, {
              name: "meetingsId",
              value: formData.meetingsId,
              onChange: handleInputChange
            })]
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.productsUsed"
              })
            }), /*#__PURE__*/_jsx(UIFormControl, {
              label: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.products"
              }),
              children: /*#__PURE__*/_jsx(ProductSelect, {
                value: formData.products,
                onChange: handleProductsChange
              })
            })]
          })]
        })
      }), /*#__PURE__*/_jsxs(UISection, {
        className: "m-bottom-12",
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.editUser.setAGoal"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.editUser.setAGoalDescription"
          })
        }), /*#__PURE__*/_jsxs(Columns, {
          equalHeight: true,
          block: true,
          children: [/*#__PURE__*/_jsxs(GoalButton, {
            type: "radio",
            name: "networkGoal",
            selected: formData.networkGoal === Goal.Jobseeking,
            value: Goal.Jobseeking,
            onChange: handleGoalChange,
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.jobOpportunities"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.jobOpportunitiesDescription"
              })
            })]
          }), /*#__PURE__*/_jsxs(GoalButton, {
            type: "radio",
            name: "networkGoal",
            selected: formData.networkGoal === Goal.Hiring,
            value: Goal.Hiring,
            onChange: handleGoalChange,
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.hiring"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.hiringDescription"
              })
            })]
          }), /*#__PURE__*/_jsxs(GoalButton, {
            type: "radio",
            name: "networkGoal",
            selected: formData.networkGoal === Goal.Upskilling,
            value: Goal.Upskilling,
            onChange: handleGoalChange,
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.skills"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.skillsDescription"
              })
            })]
          }), /*#__PURE__*/_jsxs(GoalButton, {
            type: "radio",
            name: "networkGoal",
            selected: formData.networkGoal === Goal.Troubleshooting,
            value: Goal.Troubleshooting,
            onChange: handleGoalChange,
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.troubleshooting"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.troubleshootingDescription"
              })
            })]
          }), /*#__PURE__*/_jsxs(GoalButton, {
            type: "radio",
            name: "networkGoal",
            selected: formData.networkGoal === Goal.Connect,
            value: Goal.Connect,
            onChange: handleGoalChange,
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.meetingPeople"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.meetingPeopleDescription"
              })
            })]
          })]
        })]
      }), /*#__PURE__*/_jsxs(UISection, {
        className: "m-bottom-12",
        children: [/*#__PURE__*/_jsx(H4, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.editUser.visibilityPreferences"
          })
        }), /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.editUser.visibilityPreferencesDescription"
          })
        }), /*#__PURE__*/_jsxs(UIToggleGroup, {
          name: "public",
          children: [/*#__PURE__*/_jsx(UIRadioInput, {
            value: true,
            checked: formData.public,
            onChange: handleInputChange,
            description: /*#__PURE__*/_jsx(MicroCopy, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.publicDescription"
              })
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.public"
            })
          }), /*#__PURE__*/_jsx(UIRadioInput, {
            value: false,
            checked: !formData.public,
            "data-test-id": "edit-profile-private-check",
            onChange: handleInputChange,
            description: /*#__PURE__*/_jsx(MicroCopy, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "connectDotCom.editUser.privateDescription"
              })
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.editUser.private"
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(UIButton, {
        use: "primary",
        type: "submit",
        disabled: !canSubmit,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "connectDotCom.editUser.saveChanges"
        })
      })]
    })
  });
};

export default EditUserForm;